<script setup>
import { calculateDDay } from '@/utils/dateFormatter';
import IconArrow08 from '@/components/svg/IconArrow08.vue';
import CoverCalendarDecor from './CoverCalendarDecor.vue';
import { useAnalysisStore } from '@/store/modules/analysis';
import { addContentLogAPI } from '@/api/logServiceApi';

const analysisStore = useAnalysisStore();

const props = defineProps({
  hasPermission: {
    required: true,
  },
  yongsinLightLogData: {
    required: true,
  },
  loadingTicket: {
    required: true,
  },
});

const emit = defineEmits(['goPopAnalysis']);

const logData = computed(() => {
  if (!props.yongsinLightLogData?.length) return null;
  return props.yongsinLightLogData[0];
});

const dDay = computed(() => calculateDDay(logData.value?.expiryDate));

/** 목차 리스트 */
const contentList = [
  { label: '📖 용신이란 무엇일까요?' },
  { label: '1. 내 사주에서 나를 돕는 기운!' },
  { label: '2. 나를 도와주는 중심 기운과 보조 기운' },
  { label: '3. 오행의 성향과 나에게 힘을 주는 오행' },
  { label: '4. 나는 어떤 타입일까?' },
  { label: '5. 지금 나에게 흐르는 기운' },
  { label: '6. 생활 속 활용 아이디어' },
];

const goPopAnalysis = () => emit('goPopAnalysis');

const goToChildMenu = (contentIdx) => {
  addContentLogAPI({
    contentType: props.hasPermission ? 'ay_yongsin_light_toc_paid_opt3' : 'ay_yongsin_light_toc_free_opt3',
    option3: String(contentIdx),
  });
  if (contentIdx >= 1 && !props.hasPermission) {
    goPopAnalysis();
    return;
  }
  analysisStore.navigateToContent('/ys-light', contentIdx);
};
</script>

<template>
  <div class="si-overview si-overview--yongsin-light">
    <!-- 왼쪽: 표지 -->
    <div class="si-overview__cover">
      <div class="si-overview__cover-inner">
        <!-- 헤더 -->
        <div class="si-overview__header">
          <div class="si-overview__title">
            <div class="si-overview__name">
              <span class="si-overview__name-text">{{ analysisStore.userName || '-' }}</span>
              <span class="si-overview__name-suffix">님의</span>
            </div>
            <div class="si-overview__service">나를 돕는 기운(용신)</div>
          </div>
          <div class="si-overview__badge">
            <template v-if="!loadingTicket && hasPermission">
              <span class="si-overview__badge-tag" v-if="logData?.expiryDate">
                {{ dDay }}
              </span>
              <span class="si-overview__badge-tag" v-else>소장용</span>
              <span class="si-overview__badge-date" v-if="logData?.expiryDate">
                ~ {{ logData?.expiryDate }} 까지
              </span>
            </template>
            <template v-if="!loadingTicket && !hasPermission">
              <span class="si-overview__badge-tag">구매 후 이용가능</span>
            </template>
          </div>
        </div>

        <!-- 장식용 캘린더 (개별 요소 배치 + 애니메이션) -->
        <div class="si-overview__img">
          <CoverCalendarDecor />
        </div>
      </div>
    </div>

    <!-- 오른쪽: 목차 -->
    <div class="si-overview__toc">
      <div class="si-overview__toc-title">목차</div>
      <div class="si-overview__toc-list">
        <button
          v-for="(item, idx) in contentList"
          :key="idx"
          class="si-overview__toc-item"
          :class="{ 'si-overview__toc-item--locked': !hasPermission }"
          @click="goToChildMenu(idx)"
        >
          {{ item.label }}
          <div class="si-overview__toc-icon">
            <IconArrow08 />
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/nangman_style/_saju-insight-overview.scss';

.si-overview--yongsin-light {
  .si-overview__header {
    gap: 12px;
  }

  .si-overview__service {
    color: #333;
  }

  .si-overview__name {
    color: #333;
  }

  .si-overview__cover-inner {
    gap: 20px;
  }

  .si-overview__img {
    flex: none;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    min-height: 0;
    overflow: visible;
    width: calc(100% + 72px);
    margin-left: -36px;
    margin-right: -36px;
  }
}
</style>
